import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';

import PageLoader from '../components/PageLoader';
import { getAuthHeaders, getPaymentsBaseUrl, isB2BMode } from '../utils/bookingsBase';

export default function WorldlineReturn() {
  const params = useParams();
  const bookingId = params.bookingId || params.id;
  const location = useLocation();
  const history = useHistory();

  const [error, setError] = useState(null);

  useEffect(() => {
    const search = new URLSearchParams(location.search);

    const hostedCheckoutId = search.get('hostedCheckoutId');
    const guestTokenFromUrl = search.get('t');
    const errorParam = search.get('error');

    /**
     * Si le token guest est renvoyé dans l'URL, on le sauvegarde.
     * Exemple attendu :
     * /paiement/worldline/return/:bookingId?hostedCheckoutId=xxx&t=TOKEN
     */
    if (guestTokenFromUrl) {
      try {
        localStorage.setItem('booking_guest_token', guestTokenFromUrl);
      } catch (e) {
        console.warn('BOOKING_GUEST_TOKEN_STORE_FAILED', e);
      }
    }

    const b2b = isB2BMode();

    const confirmationUrl = b2b
      ? `/b2b/confirmation/${bookingId}`
      : `/confirmation/${bookingId}`;

    const failUrl = b2b
      ? `/b2b/paiement-refuse/${bookingId}`
      : `/paiement-refuse/${bookingId}`;

    const cancelledUrl = b2b
      ? `/b2b/paiement-annule/${bookingId}`
      : `/paiement-annule/${bookingId}`;

    const pendingPaymentUrl = b2b
      ? `/b2b/paiement-en-cours/${bookingId}`
      : `/paiement-en-cours/${bookingId}`;

    const bookingProcessingUrl = b2b
      ? `/b2b/reservation-en-cours/${bookingId}`
      : `/reservation-en-cours/${bookingId}`;

    console.log('[WORLDLINE_RETURN]', {
      bookingId,
      hostedCheckoutId,
      search: location.search,
      b2b,
    });

    /**
     * Si Worldline retourne un paramètre error,
     * on considère que le paiement est refusé / annulé.
     */
    if (errorParam) {
      const errorValue = String(errorParam || '').toLowerCase();

      const isCancelled =
        errorValue.includes('cancel') ||
        errorValue.includes('canceled') ||
        errorValue.includes('cancelled') ||
        errorValue.includes('annul') ||
        errorValue.includes('abandon');

      history.replace(isCancelled ? cancelledUrl : failUrl);
      return undefined;
    }

    /**
     * Sans bookingId ou hostedCheckoutId,
     * on ne peut pas confirmer le paiement côté backend.
     */
    if (!bookingId || !hostedCheckoutId) {
      setError('Paramètres de retour de paiement invalides.');
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const base = getPaymentsBaseUrl();
        const headers = getAuthHeaders();

        console.log('[WORLDLINE_RETURN_CONFIRM_REQUEST]', {
          url: `${base}/worldline/${bookingId}/confirm`,
          bookingId,
          hostedCheckoutId,
          hasBookingToken: !!headers?.['X-Booking-Token'],
          hasAuthorization: !!headers?.Authorization,
        });

        const { data } = await axios.post(
          `${base}/worldline/${bookingId}/confirm`,
          { hostedCheckoutId },
          {
            headers,
            timeout: 60000,
          },
        );

        if (cancelled) return;

        console.log('[WORLDLINE_RETURN_CONFIRM_RESPONSE]', data);

        /**
         * Réponse inattendue du backend.
         */
        if (!data?.ok) {
          history.replace(failUrl);
          return;
        }

        /**
         * 1) Paiement refusé / échoué.
         */
        if (
          data.paymentStatus === 'cancelled' ||
          data.bookingStatus === 'payment_cancelled' ||
          data.cancelled === true ||
          data.paymentCancelled === true
        ) {
          history.replace(cancelledUrl);
          return;
        }

        /**
         * 2) Paiement encore en attente chez Worldline.
         */
        if (data.paymentStatus === 'pending') {
          history.replace(pendingPaymentUrl);
          return;
        }

        /**
         * 3) Paiement OK + réservation PGI créée.
         */
        if (
          data.paymentStatus === 'paid' &&
          (data.pgiStatus === 'success' || !!data.pgiBookingCode)
        ) {
          history.replace(confirmationUrl);
          return;
        }

        /**
         * 4) Paiement OK mais réservation PGI pas encore finalisée.
         *
         * Exemple :
         * paymentStatus = paid
         * pgiStatus = failed / processing / not_started
         * pgiBookingCode = null
         */
        if (data.paymentStatus === 'paid') {
          history.replace(bookingProcessingUrl);
          return;
        }

        /**
         * Fallback sécurité.
         */
        history.replace(failUrl);
      } catch (e) {
        if (cancelled) return;

        console.error('WORLDLINE_RETURN_FAILED', {
          message: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
          code: e?.code,
        });

        /**
         * Important :
         * Ne pas afficher une erreur bloquante ici.
         *
         * Le paiement peut être accepté côté Worldline,
         * mais le backend peut répondre trop tard, retourner 502,
         * ou être encore en train de créer la réservation PGI.
         *
         * Donc on redirige vers la page de traitement réservation.
         */
        history.replace(pendingPaymentUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId, location.search, history]);

  if (error) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="alert alert-danger text-center">
              <h4 className="mb-3">Erreur</h4>
              <p className="mb-0">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <PageLoader show={true} label="Traitement de votre paiement…" />;
}
